import type { Event, EventHint } from "@sentry/core";
import type { RuntimeConfig } from "nuxt/schema";

export type SentryBooleanLike = boolean | string | number | null | undefined;

export interface PublicSentryConfig {
  dsn?: string;
  enabled: boolean;
  debug: boolean;
  environment: string;
  release?: string;
  tracesSampleRate: number;
  replaysSessionSampleRate: number;
  replaysOnErrorSampleRate: number;
  replayMaskAllInputs: boolean;
  replayMaskAllText: boolean;
  replayBlockAllMedia: boolean;
  allowReplay: boolean;
  captureConsole: boolean;
}

const benignErrorNames = new Set(["AbortError", "NetworkError", "ChunkLoadError"]);
const benignMessages = [
  "ResizeObserver loop limit exceeded",
  "Non-Error promise rejection captured",
  "Failed to construct",
  "Load failed",
  "Loading chunk",
  "Loading CSS chunk",
  "TypeError: cancelled",
  "TypeError: Failed to fetch",
  "TypeError: NetworkError",
  "Script error"
];

const PRODUCTION_ENVIRONMENTS = new Set(["prod", "rec"]);

const safeProcessEnv = (key: string): string | undefined => {
  if (typeof process !== "undefined" && process.env) {
    return process.env[key] ?? undefined;
  }
  return undefined;
};

const normalizeNullableString = (value: unknown): string | undefined => {
  if (value === null || value === undefined) {
    return undefined;
  }

  const normalized = String(value).trim();
  if (!normalized) {
    return undefined;
  }

  const lowerCased = normalized.toLowerCase();
  if (lowerCased === "undefined" || lowerCased === "null" || normalized === "<invalid>") {
    return undefined;
  }

  return normalized;
};

export const parseBoolean = (value: SentryBooleanLike, fallback = false): boolean => {
  if (typeof value === "boolean") {
    return value;
  }

  if (typeof value === "number") {
    return value !== 0;
  }

  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    if (["1", "true", "yes", "on"].includes(normalized)) {
      return true;
    }

    if (["0", "false", "no", "off"].includes(normalized)) {
      return false;
    }

    return fallback;
  }

  return fallback;
};

export const parseNumber = (value: unknown, fallback: number): number => {
  if (typeof value === "number" && !Number.isNaN(value)) {
    return value;
  }

  if (typeof value === "string") {
    const parsed = Number.parseFloat(value);
    if (!Number.isNaN(parsed)) {
      return parsed;
    }
  }

  return fallback;
};

export const resolveEnvironment = (runtimeConfig: RuntimeConfig): string => {
  return (
    normalizeNullableString(runtimeConfig.public?.sentry?.environment) ??
    normalizeNullableString(runtimeConfig.public?.appVersion) ??
    normalizeNullableString(safeProcessEnv("APP_VERSION")) ??
    normalizeNullableString(safeProcessEnv("NODE_ENV")) ??
    "dev"
  );
};

export const resolveRelease = (runtimeConfig: RuntimeConfig): string | undefined => {
  return (
    normalizeNullableString(runtimeConfig.public?.sentry?.release) ??
    normalizeNullableString(runtimeConfig.public?.appVersion) ??
    normalizeNullableString(safeProcessEnv("APP_VERSION"))
  );
};

const isLocalEnvironment = (environment: string): boolean => {
  return ["loc", "dev"].includes(environment.toLowerCase());
};

const resolveAllowReplay = (runtimeConfig: RuntimeConfig, enabled: boolean): boolean => {
  return parseBoolean(
    runtimeConfig.public?.sentry?.allowReplay ?? safeProcessEnv("NUXT_PUBLIC_SENTRY_ALLOW_REPLAY") ?? enabled,
    enabled
  );
};

export const getPublicSentryConfig = (runtimeConfig: RuntimeConfig): PublicSentryConfig => {
  const environment = resolveEnvironment(runtimeConfig);
  const release = resolveRelease(runtimeConfig);
  const localEnvironment = isLocalEnvironment(environment);
  const envKey = environment.toLowerCase();
  const productionLike = PRODUCTION_ENVIRONMENTS.has(envKey);
  const defaultEnabled = productionLike;
  const debug = parseBoolean(
    runtimeConfig.public?.sentry?.debug ??
    safeProcessEnv("NUXT_PUBLIC_SENTRY_DEBUG") ??
    localEnvironment,
    localEnvironment
  );
  const enabled = parseBoolean(
    runtimeConfig.public?.sentry?.enabled ??
    safeProcessEnv("NUXT_PUBLIC_SENTRY_ENABLED") ??
    (safeProcessEnv("NUXT_PUBLIC_SENTRY_DSN") && productionLike ? "true" : undefined),
    defaultEnabled
  );
  const allowReplay = resolveAllowReplay(runtimeConfig, enabled && productionLike);
  const defaultTraceSampleRate = debug ? 1 : productionLike ? 0.2 : localEnvironment ? 1 : 0.02;

  return {
    dsn:
      runtimeConfig.public?.sentry?.dsn ??
      safeProcessEnv("NUXT_PUBLIC_SENTRY_DSN") ??
      undefined,
    enabled,
    debug,
    environment,
    release,
    tracesSampleRate: parseNumber(
      runtimeConfig.public?.sentry?.tracesSampleRate ?? safeProcessEnv("NUXT_PUBLIC_SENTRY_TRACES_SAMPLE_RATE"),
      defaultTraceSampleRate
    ),
    replaysSessionSampleRate: parseNumber(
      runtimeConfig.public?.sentry?.replaysSessionSampleRate ?? safeProcessEnv("NUXT_PUBLIC_SENTRY_REPLAYS_SESSION_SAMPLE_RATE"),
      allowReplay ? (productionLike ? 0.02 : 0.005) : 0
    ),
    replaysOnErrorSampleRate: parseNumber(
      runtimeConfig.public?.sentry?.replaysOnErrorSampleRate ?? safeProcessEnv("NUXT_PUBLIC_SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE"),
      allowReplay ? (productionLike ? 0.2 : 0.05) : 0
    ),
    replayMaskAllInputs: parseBoolean(
      runtimeConfig.public?.sentry?.replayMaskAllInputs ?? safeProcessEnv("NUXT_PUBLIC_SENTRY_REPLAY_MASK_INPUTS") ?? true,
      true
    ),
    replayMaskAllText: parseBoolean(
      runtimeConfig.public?.sentry?.replayMaskAllText ?? safeProcessEnv("NUXT_PUBLIC_SENTRY_REPLAY_MASK_TEXT") ?? true,
      true
    ),
    replayBlockAllMedia: parseBoolean(
      runtimeConfig.public?.sentry?.replayBlockAllMedia ?? safeProcessEnv("NUXT_PUBLIC_SENTRY_REPLAY_BLOCK_MEDIA") ?? true,
      true
    ),
    allowReplay,
    captureConsole: parseBoolean(
      runtimeConfig.public?.sentry?.captureConsole ?? safeProcessEnv("NUXT_PUBLIC_SENTRY_CAPTURE_CONSOLE") ?? false,
      false
    )
  };
};

export const shouldEnableClientSentry = (config: PublicSentryConfig): boolean => {
  if (!config.dsn) {
    return false;
  }

  if (!config.enabled) {
    return false;
  }

  return true;
};

export const isBenignClientEvent = (event: Event, hint?: EventHint): boolean => {
  const originalException = hint?.originalException;
  if (originalException instanceof Error) {
    if (benignErrorNames.has(originalException.name)) {
      return true;
    }

    if (benignMessages.some(message => originalException.message?.includes(message))) {
      return true;
    }
  }

  if (event.message && benignMessages.some(message => event.message.includes(message))) {
    return true;
  }

  return false;
};

export const denyUrls: Array<string | RegExp> = [
  /extensions\//i,
  /^chrome:\/\//i,
  /^chrome-extension:\/\//i,
  /^moz-extension:\/\//i,
  /^safari-extension:\/\//i,
  /gtm\.js/,
  /axeptio/i,
  /googletagmanager\.com/
];

export const ignoreErrors: string[] = [
  "ChunkLoadError",
  "Loading chunk",
  "Loading CSS chunk",
  "Script error",
  "TypeError: cancelled",
  "TypeError: Failed to fetch",
  "TypeError: NetworkError when attempting to fetch resource"
];

export const extraClientTags = (config: PublicSentryConfig): Record<string, string> => {
  return {
    app_env: config.environment
  };
};
